import type { PresentationPrefs, ReplacePresentationPrefs } from "./types.js";

/**
 * The browser's zone, used only while no presentation row has been persisted
 * (version 0). Falls back to UTC when the runtime cannot say.
 */
export function detectedTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export type PresentationEdit = Partial<
  Pick<PresentationPrefs, "theme" | "locale" | "timeZone">
>;

/**
 * The ONE way the shell builds a presentation PUT (PB-5134, d29).
 *
 * Every write is a full replacement, so it is composed from the freshest cached
 * row plus the single field being changed. While the row is unpersisted
 * (version 0) the server default `timeZone: "UTC"` is a placeholder, not a
 * choice: the detected browser zone is written instead, so the first persisted
 * row — even one created by a theme toggle — carries the user's real zone and
 * a later "trust the stored zone once version >= 1" rule holds.
 */
export function composePresentationReplacement(
  current: PresentationPrefs,
  edit: PresentationEdit,
  detectZone: () => string = detectedTimeZone,
): ReplacePresentationPrefs {
  const timeZone =
    edit.timeZone ?? (current.version === 0 ? detectZone() : current.timeZone);
  return {
    theme: edit.theme ?? current.theme,
    locale: edit.locale ?? current.locale,
    timeZone,
    expectedVersion: current.version,
  };
}

/** The zone the UI should display and format with for this row (d27/d29). */
export function effectiveTimeZone(
  current: PresentationPrefs | undefined,
  detectZone: () => string = detectedTimeZone,
): string {
  return current && current.version >= 1 ? current.timeZone : detectZone();
}
